import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    OnDestroy,
    OnInit,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Event, NavigationEnd, ResolveStart, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom, Subscription, take } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { CreateEntityButtonComponent } from '@connect-services/client/components/create-entity-button/create-entity-button.component';
import { EntityTableComponent } from '@connect-services/client/components/entity-table/entity-table.component';
import { ImmersiveViewComponent } from '@connect-services/client/components/immersive-view/immersive-view.component';
import { ImmersiveViewSelectionComponent } from '@connect-services/client/components/immersive-view/immersive-view-selection/immersive-view-selection.component';
import { MapBaseSource, MapComponent, MapLayerSource } from '@connect-services/client/components/map/map.component';
import { SidebarComponent } from '@connect-services/client/components/sidebar/sidebar.component';
import { TopbarComponent } from '@connect-services/client/components/topbar/topbar.component';
import { VersionComponent } from '@connect-services/client/components/version/version.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    LayerBroadCastChannelData,
    LayerBroadcastChannelTargetEnum,
    LayersBroadcastEvent,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { GeomLayer } from '@connect-services/client/models/geom-layer.model';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { RouteCompletePathEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ContentToolbarComponent } from '@connect-services/client/pages/home/content-toolbar/content-toolbar.component';
import { SESSION_STORE_KEY } from '@connect-services/client/resolvers/session.resolver';
import { Subscription as ApiSubscription } from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { BroadcastChannelService, SynopticMapData } from '@connect-services/client/services/broadcast-channel.service';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { DataExportService } from '@connect-services/client/services/data-export.service';
import { DepositService } from '@connect-services/client/services/deposit.service';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { EntityCreateUiService } from '@connect-services/client/services/entity-create-ui.service';
import { FaqService } from '@connect-services/client/services/faq.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { ImmersiveViewService } from '@connect-services/client/services/immersive-view/immersive-view.service';
import { ImmersiveViewUiService } from '@connect-services/client/services/immersive-view/immersive-view-ui.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { LayerEditBadgeService } from '@connect-services/client/services/layer-edit-badge.service';
import { EditModeCapabilities, MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapPopupService } from '@connect-services/client/services/map-popup.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { NotificationService } from '@connect-services/client/services/notification.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { PinService } from '@connect-services/client/services/pin.service';
import { ShapingProjectService } from '@connect-services/client/services/project/shaping/shaping-project.service';
import { CreateProjectShapingStateService } from '@connect-services/client/services/project/shaping/shaping-state/create-project-shaping-state.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { StatisticService } from '@connect-services/client/services/statistic.service';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { MapSynopticRedirectionService } from '@connect-services/client/services/synoptic/map-synoptic-redirection.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SynopticGraphService } from '@connect-services/client/services/synoptic/synoptic-graph.service';
import { TableBroadcastService } from '@connect-services/client/services/table-broadcast.service';
import { TableEditModeService } from '@connect-services/client/services/table-edit-mode.service';
import { WfsRequestService } from '@connect-services/client/services/wfs-request.service';
import { Layer, LayersService, PermissionAliasEnum } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters, paginationMaxTake } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ContentToolbarComponent,
        CreateEntityButtonComponent,
        EntityTableComponent,
        ImmersiveViewComponent,
        ImmersiveViewSelectionComponent,
        MapComponent,
        RessourcesModule,
        SidebarComponent,
        TopbarComponent,
        VersionComponent,
    ],
    selector: 'app-home',
    styleUrl: './home.component.scss',
    templateUrl: './home.component.html',
})
export class HomeComponent implements OnDestroy, OnInit {
    public readonly baseCapabilities = computed<EditModeCapabilities>(() => {
        const canCreate = this.hasEditPermission(PermissionAliasEnum.MANAGE_EDIT_MODE_CREATE);
        const canUpdate = this.hasEditPermission(PermissionAliasEnum.MANAGE_EDIT_MODE_UPDATE);
        const canDelete = this.hasEditPermission(PermissionAliasEnum.MANAGE_EDIT_MODE_DELETE);
        const enabled = (canCreate || canUpdate || canDelete) && this.hasEditSubscription() && !this.isEditMapDisable();

        return {
            canCreate,
            canDelete,
            canUpdate,
            enabled,
        };
    });
    public readonly hasExternalLayerPendingEdits = computed<boolean>(() => {
        if (!this.layerName()) {
            return false;
        }

        return this.layerEditBadgeService.hasPendingEditsForLayerName(this.layerName());
    });
    public readonly isEditModeActive = computed(() => this.projectEditModeService.isEditModeActive());
    public readonly isExternalTableEditActive = computed(() => this.tableEditModeService.isExternalTableEditActive());
    public readonly isPanelScrollableY = computed(() => {
        return this.panelService.currentPanel() !== ActionsEnum.SETTINGS;
    });
    public readonly isSubscriptionLoaded = signal<boolean>(false);
    public readonly layerName: WritableSignal<string | null> = signal(null);

    protected readonly ActionsEnum = ActionsEnum;
    protected readonly canExportFromServices = computed(() => {
        return (
            !this.createFieldInterventionService.isCreateFieldIntervention() &&
            !this.entitiesTableByLayerService.isFieldInterventionSelectionActive() &&
            !this.statisticService.isStatisticActive()
        );
    });
    protected readonly contentLayoutService = inject(ContentLayoutService);
    protected readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    protected readonly createState = computed(() => {
        return this.entityCreateUiService.getCreateState({
            capabilities: this.baseCapabilities(),
            isEditModeActive: this.isExternalTableEditActive(),
            layer: this.currentLayer(),
        });
    });
    protected readonly currentLayer = computed(() => {
        const layerName = this.broadcastChannelService.currentLayer();
        if (!layerName) {
            return null;
        }

        return this.projectEntityTableService.getLayerByName(layerName) ?? null;
    });
    protected readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    protected readonly faqService = inject(FaqService);
    protected readonly geographicFilterService = inject(GeographicFilterService);
    protected readonly homeService = inject(HomeService);
    protected readonly immersiveViewService = inject(ImmersiveViewService);
    protected readonly immersiveViewUiService = inject(ImmersiveViewUiService);
    protected readonly panelService = inject(PanelService);
    protected readonly pinService = inject(PinService);
    protected readonly selectSynopticService = inject(SelectSynopticService);
    protected readonly sourcesService = inject(SourcesService);
    protected readonly statisticService = inject(StatisticService);
    protected readonly tableEditModeService = inject(TableEditModeService);
    protected readonly wfsUrlRequestService = inject(WfsRequestService);

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly dataExportService = inject(DataExportService);
    private readonly depositService = inject(DepositService);
    private readonly entityCreateUiService = inject(EntityCreateUiService);
    private readonly fieldInterventionRegExp: RegExp = new RegExp(`\\/${RoutePathEnum.FIELD_INTERVENTIONS}`, 'i');
    private readonly hasEditSubscription = computed<boolean>(() => {
        return !!this.subscription()?.options?.hasEditMode;
    });
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly isEditMapDisable = signal<boolean>(false);
    private readonly layerEditBadgeService = inject(LayerEditBadgeService);
    private readonly layersBroadcastChannel: BroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private readonly layerService = inject(LayerService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapPopupService = inject(MapPopupService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly mapSynopticRedirectionService = inject(MapSynopticRedirectionService);
    private readonly matDialog = inject(MatDialog);
    private readonly notificationService = inject(NotificationService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly projectEntityTableService = inject(ProjectEntityTableService);
    private readonly router = inject(Router);
    private readonly routerNavigationEndSubscription: Subscription | undefined = undefined;
    private readonly sdkLayersService = inject(LayersService);
    private readonly securityService = inject(SecurityService);
    private readonly shapingProjectService = inject(ShapingProjectService);
    private readonly sourceRefreshSubscription: Subscription;
    private readonly statisticRegExp: RegExp = new RegExp(`\\/${RoutePathEnum.STATISTICS}`, 'i');
    private readonly subscription: WritableSignal<ApiSubscription | undefined> = signal<ApiSubscription | undefined>(
        undefined,
    );
    private readonly subscriptionService = inject(SubscriptionService);
    private readonly synopticBroadcastChannel: BroadcastChannel = new BroadcastChannel(
        BroadcastChannelNameEnum.SYNOPTIC,
    );
    private readonly synopticGraphService = inject(SynopticGraphService);
    private readonly tableBroadcastService = inject(TableBroadcastService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.router.events.pipe(filter((event: Event) => event instanceof ResolveStart)).subscribe(() => {
            this.wfsUrlRequestService.cancelAllWfsRequests();
        });

        this.routerNavigationEndSubscription = this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const isFieldInterventionPage = this.fieldInterventionRegExp.test(event.urlAfterRedirects);
                const isStatisticPage = this.statisticRegExp.test(event.urlAfterRedirects);

                this.isEditMapDisable.set(isFieldInterventionPage || isStatisticPage);
                this.geographicFilterService.isGeographicFilterAllowedOnComponent.set(
                    !(isFieldInterventionPage || isStatisticPage),
                );
            });

        const layerName = this.activatedRoute.snapshot.queryParamMap.get('layerName');
        if (layerName) {
            this.layerName.set(decodeURI(layerName));
        }

        this.sourceRefreshSubscription = this.notificationService.sourceRefreshNeeded
            .pipe(take(1))
            .subscribe(async () => {
                await this.refreshDepositLayers();
            });

        this.activatedRoute.params.subscribe(async () => {
            if (!this.projectBloc.value()) {
                this.showProjectDialog();

                return;
            }
            await this.homeService.initSources(true);
            this.mapSourceService.fitMapBounds();
        });

        this.listenSynopticBroadcastChannelMessages();
        this.listenLayersBroadcastChannelMessages();

        effect(async () => {
            if (!untracked(() => this.projectBloc.value())) {
                return;
            }

            const allLayer = await this.layerService.getAllLayers({ types: [Layer.TypeEnum.TABLE] });
            this.layerService.setLayers(allLayer.items);

            untracked(() => {
                this.dataExportService.resetControls();
            });

            if (untracked(() => this.mapPopupService.isPopupOpen())) {
                untracked(() => this.mapPopupService.closePopup());
            }
        });

        effect(() => {
            this.mapEditModeService.updateEditPermissions({
                canCreate: this.baseCapabilities().canCreate,
                canDelete: this.baseCapabilities().canDelete,
                canUpdate: this.baseCapabilities().canUpdate,
            });
        });

        effect(() => {
            if (this.homeService.isExternalPage()) {
                return;
            }

            const isEditModeActive = this.projectEditModeService.isEditModeActive();
            this.tableBroadcastService.syncEditModeToExternal(isEditModeActive);
        });
    }

    public ngOnDestroy(): void {
        if (this.sourceRefreshSubscription) {
            this.sourceRefreshSubscription.unsubscribe();
        }

        if (this.routerNavigationEndSubscription) {
            this.routerNavigationEndSubscription.unsubscribe();
        }

        this.synopticBroadcastChannel.close();
        this.tableBroadcastService.closeChannel();
    }

    public async ngOnInit(): Promise<void> {
        const subscription: ApiSubscription = await untracked(() => this.subscriptionService.getSubscription());
        this.subscription.set(subscription);
        this.isSubscriptionLoaded.set(true);

        this.initializeTableBroadcast();

        this.ensureExternalSessionId();
        this.ensureExternalEditModeState();
    }

    public async onCreateClickExternal(): Promise<void> {
        const layer = this.currentLayer();
        if (!layer || !this.baseCapabilities().canCreate) {
            return;
        }

        await this.projectEntityTableService.openCreateEntityPanel(layer.id, layer.name);
    }

    private applyExternalEditModeState(isEditModeActive: boolean): void {
        const capabilities = this.baseCapabilities();

        if (!capabilities.enabled) {
            return;
        }

        if (isEditModeActive) {
            this.enableExternalEditMode(capabilities);

            return;
        }

        this.disableExternalEditMode();
    }

    private applyRequestedEditModeFromExternal(isEditModeActive: boolean): void {
        if (isEditModeActive) {
            this.projectEditModeService.activateEditMode();

            return;
        }

        this.projectEditModeService.deactivateEditMode();
    }

    private disableExternalEditMode(): void {
        if (!this.tableEditModeService.isExternalTableEditActive()) {
            return;
        }

        this.tableEditModeService.leaveTableEditModeExternalView();
        this.tableEditModeService.isEditModeActive.set(false);
    }

    private enableExternalEditMode(capabilities: EditModeCapabilities): void {
        if (this.tableEditModeService.isExternalTableEditActive()) {
            return;
        }

        const entityId = this.projectEntityTableService.currentPanelEntity() ?? null;
        const canEditMode = this.tableEditModeService.enterTableEditModeExternalView(capabilities, entityId);

        this.tableEditModeService.isEditModeActive.set(canEditMode);
    }

    private ensureExternalEditModeState(): void {
        if (!this.homeService.isExternalPage()) {
            return;
        }

        this.tableBroadcastService.requestEditModeStateFromHome();
    }

    private ensureExternalSessionId(): void {
        if (!this.homeService.isExternalPage()) {
            return;
        }

        const existingSessionId = sessionStorage.getItem(SESSION_STORE_KEY);
        if (existingSessionId) {
            return;
        }

        this.tableBroadcastService.requestSessionContextFromHome();
    }

    private handleLayerWindowCheck(data: LayersBroadcastEvent): void {
        if (!this.homeService.isExternalPage() || data.message !== BroadcastChannelMessageEnum.REQUEST_LAYER_WINDOW) {
            return;
        }

        const askedLayer = data.additionalData;

        if (typeof askedLayer !== 'string' || askedLayer !== this.layerName()) {
            return;
        }

        this.layersBroadcastChannel.postMessage({
            additionalData: askedLayer,
            message: BroadcastChannelMessageEnum.LAYER_WINDOW_EXISTS,
        });
    }

    private async handleMoveSynoptic(data: LayersBroadcastEvent): Promise<void> {
        if (
            data.source !== BroadcastChannelSourceEnum.LAYER ||
            data.message !== BroadcastChannelMessageEnum.MOVE_SYNOPTIC
        ) {
            return;
        }

        const properties = data.additionalData;

        if (!properties || typeof properties !== 'object') {
            return;
        }

        const isExternalPage = this.homeService.isExternalPage();
        const shouldHandleOnExternalPage =
            properties.target === LayerBroadcastChannelTargetEnum.EXTERNAL_SYNOPTIC &&
            isExternalPage &&
            !this.layerName() &&
            !this.homeService.isExternalBoxPlanPage();
        const shouldHandleOnHomePage = properties.target === LayerBroadcastChannelTargetEnum.HOME && !isExternalPage;

        if (!shouldHandleOnExternalPage && !shouldHandleOnHomePage) {
            return;
        }

        if (shouldHandleOnHomePage && this.broadcastChannelService.isSynopticOpenInExternalPage()) {
            this.layersBroadcastChannel.postMessage(<BroadcastChannelEvent<LayerBroadCastChannelData>>{
                ...data,
                additionalData: {
                    ...properties,
                    target: LayerBroadcastChannelTargetEnum.EXTERNAL_SYNOPTIC,
                },
            });

            return;
        }

        this.selectSynopticService.isEditMode.set(false);

        if (shouldHandleOnHomePage) {
            await this.mapSynopticRedirectionService.redirectMapEntityToSynoptic(
                properties.deposit,
                properties.znUuidAttributeValue,
                properties.dataEntity,
            );

            return;
        }

        await this.selectSynopticService.changeSynopticIfNeeded(properties);

        setTimeout(() => this.synopticGraphService.focusNode(properties.dataEntity), 100);
    }

    private hasEditPermission(permission: PermissionAliasEnum): boolean {
        return this.hasPermissionsService.checkPermissions([permission]);
    }

    private initializeTableBroadcast(): void {
        if (this.homeService.isExternalPage()) {
            this.tableBroadcastService.initForExternalView({
                onCloseRequested: () => window.close(),
                onEditModeStateReceived: (isEditModeActive: boolean) =>
                    this.applyExternalEditModeState(isEditModeActive),
                onSessionIdReceived: (sessionId: string) => sessionStorage.setItem(SESSION_STORE_KEY, sessionId),
            });

            return;
        }

        this.tableBroadcastService.initForHomeView({
            getEditModeActive: () => this.projectEditModeService.isEditModeActive(),
            getSessionId: () => sessionStorage.getItem(SESSION_STORE_KEY),
            onEntitiesSaved: async () => {
                await this.projectEntityTableService.fetchEntities();
                await this.mapSourceService.redrawSources();
            },
            onRequestSetEditMode: (isEditModeActive: boolean) =>
                this.applyRequestedEditModeFromExternal(isEditModeActive),
        });
    }

    private listenLayersBroadcastChannelMessages(): void {
        this.layersBroadcastChannel.onmessage = async (event: MessageEvent<LayersBroadcastEvent>): Promise<void> => {
            const data = event.data;

            AssertService.assertLayersBroadcastEvent(data);

            if (data.isClosing && data.source === BroadcastChannelSourceEnum.HOMEPAGE) {
                window.close();

                return;
            }

            switch (data.message) {
                case BroadcastChannelMessageEnum.MOVE_SYNOPTIC:
                    await this.handleMoveSynoptic(data);

                    return;

                case BroadcastChannelMessageEnum.REQUEST_LAYER_WINDOW:
                    this.handleLayerWindowCheck(data);

                    return;
            }
        };
    }

    private listenSynopticBroadcastChannelMessages(): void {
        this.synopticBroadcastChannel.onmessage = async (
            event: MessageEvent<BroadcastChannelEvent<SynopticMapData>>,
        ): Promise<void> => {
            if (
                event.data.message !== BroadcastChannelMessageEnum.MOVE_MAP ||
                event.data.source !== BroadcastChannelSourceEnum.GRAPH
            ) {
                return;
            }

            await this.broadcastChannelService.moveMapOnMessage(event.data);
        };
    }

    private openAssignProjectDialog(): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('close'),
                confirmLabel: this.translocoService.translate('accessExistingProject'),
                description: this.translocoService.translate('assignExistingProject'),
                title: this.translocoService.translate('noProjectAccessInOrganization'),
                type: ModalType.WARNING,
            },
            width: '35rem',
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            await this.router.navigate([
                ...RouteCompletePathEnum.USERS_ROLES,
                { outlets: { detailPanel: ['user', this.securityService.meSignal()!.id] } },
            ]);
        });
    }

    private openCreateOrAssignProjectDialog(): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('newProject'),
                confirmLabel: this.translocoService.translate('existingProject'),
                description: this.translocoService.translate('createOrAssignProject'),
                title: this.translocoService.translate('noProjectAccessInOrganization'),
                type: ModalType.WARNING,
            },
            width: '35rem',
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                this.shapingProjectService.openShapingProjectModal(CreateProjectShapingStateService);

                return;
            }

            await this.router.navigate([
                ...RouteCompletePathEnum.USERS_ROLES,
                { outlets: { detailPanel: ['user', this.securityService.meSignal()!.id] } },
            ]);
        });
    }

    private openCreateProjectDialog(): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('close'),
                confirmLabel: this.translocoService.translate('createProject'),
                description: this.translocoService.translate('youCreateNewProject'),
                title: this.translocoService.translate('noProjectAccessInOrganization'),
                type: ModalType.WARNING,
            },
            width: '35rem',
        });

        dialogRef.afterClosed().subscribe((result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            this.shapingProjectService.openShapingProjectModal(CreateProjectShapingStateService);
        });
    }

    private openNoPermissionsDialog(): void {
        this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: null,
                confirmLabel: this.translocoService.translate('close'),
                description: this.translocoService.translate('contactAdminForProjectAssociation'),
                title: this.translocoService.translate('noProjectAccessInOrganization'),
                type: ModalType.WARNING,
            },
            width: '35rem',
        });
    }

    private async refreshDepositLayers(): Promise<void> {
        if (!this.depositService.newDeposit() || !this.depositService.depositFolderId()) {
            return;
        }

        const layers = await firstValueFrom(
            this.sdkLayersService.layerControllerGet({
                ...defaultPaginationParameters,
                deposit: this.depositService.newDeposit()?.id,
                take: paginationMaxTake,
            }),
        );

        const layerSources = await Promise.all(
            layers.items.map(
                async (layer: Layer): Promise<MapLayerSource> =>
                    new MapLayerSource(
                        GeomLayer.fromLayer(layer),
                        await this.sourcesService.getLayerStyle(layer.layerStyle)!,
                    ),
            ),
        );

        await this.updateSources(layerSources);
    }

    private showProjectDialog(): void {
        const userPermissions = this.securityService.meSignal()?.role?.permissionAliases;
        if (!userPermissions?.length) {
            this.openNoPermissionsDialog();

            return;
        }

        const canManageProject = this.hasPermissionsService.checkPermissions([PermissionAliasEnum.MANAGE_PROJECT]);
        const canManagePerson = this.hasPermissionsService.checkPermissions([PermissionAliasEnum.MANAGE_PERSON]);

        if (canManageProject && canManagePerson) {
            this.openCreateOrAssignProjectDialog();

            return;
        }

        if (canManageProject) {
            this.openCreateProjectDialog();

            return;
        }

        if (canManagePerson) {
            this.openAssignProjectDialog();

            return;
        }

        this.openNoPermissionsDialog();
    }

    private async updateSources(layerSources: MapLayerSource[]): Promise<void> {
        this.sourcesService.sources.set([
            ...this.sourcesService.sources().filter((source: MapBaseSource<unknown>) => {
                return !layerSources.some((layerSource: MapLayerSource) => layerSource.sourceId === source.sourceId);
            }),
            ...layerSources,
        ]);

        await this.mapSourceService.drawSources(this.sourcesService.sources()!, { fitBounds: false });
    }
}
